import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import UseLangLstQuery from 'Apis/LangTxtInqApi';
import LnchStrtApi from 'Apis/LnchStrtApi';
import GameSvcLinkApi from 'Apis/GameSvcLinkApi';
import GlitchImage from 'Utils/GlitchImage';
import { Helmet } from 'react-helmet-async';
import "Assets/css/mainPage.css";
import { isMobile } from 'react-device-detect';
import ApiResultModal from 'Components/ApiResultModal';
import GameLnchMdalUi from 'Utils/GameLnchMdalUi';
import { fetchAdPayload, getKstTimestamp, useGtm } from 'hooks/useGtm';
import useMaintenanceModalGuard from 'hooks/useMaintenanceModalGuard';

const MUSIC_OWNER_KEY = 'globalMusicOwnerTabId';
const MUSIC_TIME_KEY = 'globalMusicCurrentTime';
const MUSIC_USER_PREFERENCE = 'globalMusicUserPreference';
const MUSIC_FORCE_PAUSE_EVENT = 'global-music-force-pause';
const PC_VIDEO_URLS = [
    'https://bb-cdn.onuniverse.io/web/mainpage/5e56f6f8-0225-428d-b325-335b71716751.mp4',
    'https://bb-cdn.onuniverse.io/web/mainpage/bed727fb-0a88-415d-8690-b49fcc54e826.mp4',
];
const MOBILE_VIDEO_URLS = [
    'https://bb-cdn.onuniverse.io/web/mainpage/8a81f3e5-84b1-4d9f-bda3-2f52cc2f051d.mp4',
    'https://bb-cdn.onuniverse.io/web/mainpage/14a34b81-7902-41b6-9d74-82445dfc92ba.mp4',
];
const PC_IMAGE_URLS = [
    'https://bb-cdn.onuniverse.io/web/mainpage/72cf7746-7118-435b-ac59-095bb133754d.png',
    'https://bb-cdn.onuniverse.io/web/mainpage/377f73fe-78f1-4070-a2e9-e1bcc4032c70.png',
];
const MOBILE_IMAGE_URLS = [
    'https://bb-cdn.onuniverse.io/web/mainpage/72cf7746-7118-435b-ac59-095bb133754d.png',
    'https://bb-cdn.onuniverse.io/web/mainpage/377f73fe-78f1-4070-a2e9-e1bcc4032c70.png',
];
const USE_IMAGE_FALLBACK = false;

interface GameLayoutContext {
    mntnData?: any;
    tstrData?: any;
}
declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
    }
}
const GamePackageUi = () => {
        // 모바일 가로모드 감지
        const [isLandscapeMobile, setIsLandscapeMobile] = useState(isMobile && window.innerWidth > window.innerHeight);

        useEffect(() => {
            const handleResize = () => {
                setIsLandscapeMobile(isMobile && window.innerWidth > window.innerHeight);
            };
            window.addEventListener('resize', handleResize);
            window.addEventListener('orientationchange', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('orientationchange', handleResize);
            };
        }, []);
    const { push: gtmPush } = useGtm();
    const location = useLocation();
    const { mntnData, tstrData } = (useOutletContext<GameLayoutContext>() || {});
    const { data: langData }: any = UseLangLstQuery();
    const [isImgGlitch, setIsImgGlitch] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const frameRef = useRef(0);
    const animateRef = useRef<number>();
    const hoverAudioRef = useRef<HTMLAudioElement | null>(null);
    const isIntroPlayed = useRef(false);
    const isMouseOver = useRef(false);
    const [showLaunchModal, setShowLaunchModal] = useState(false);
    const [gameMainPageInfoState, setGameMainPageInfoState] = useState<any>();
    const [pcVideoSrc] = useState(() => PC_VIDEO_URLS[Math.floor(Math.random() * PC_VIDEO_URLS.length)]);
    const [mobileVideoSrc] = useState(() => MOBILE_VIDEO_URLS[Math.floor(Math.random() * MOBILE_VIDEO_URLS.length)]);
    const [pcImageSrc] = useState(() => PC_IMAGE_URLS[Math.floor(Math.random() * PC_IMAGE_URLS.length)]);
    const [mobileImageSrc] = useState(() => MOBILE_IMAGE_URLS[Math.floor(Math.random() * MOBILE_IMAGE_URLS.length)]);
    const [useImageFallbackByError, setUseImageFallbackByError] = useState(false);

    const stopBackgroundMusic = () => {
        localStorage.setItem(MUSIC_USER_PREFERENCE, 'pause');

        const globalMusicAudio = document.querySelector('.music-player audio') as HTMLAudioElement | null;
        if (globalMusicAudio) {
            localStorage.setItem(MUSIC_TIME_KEY, `${globalMusicAudio.currentTime || 0}`);
            globalMusicAudio.pause();
        }

        localStorage.removeItem(MUSIC_OWNER_KEY);
        window.dispatchEvent(new Event(MUSIC_FORCE_PAUSE_EVENT));
    };

    const gameSvcCdState = useMemo(() => {
        const query = new URLSearchParams(location.search);
        return (query.get('gameSvcCd') || 'bbo').toLowerCase();
    }, [location.search]);

    const {
        isMaintenance,
        resultModalOpen,
        resultModalMessage,
        openResultModal,
        closeResultModal,
        runWithMaintenanceGuard,
    } = useMaintenanceModalGuard({
        mntnData: Array.isArray(mntnData) ? mntnData : [],
        targetGameSvcCd: gameSvcCdState,
        langData,
        specialModalStartKst: process.env.REACT_APP_PREREGISTER_MAINTENANCE_MODAL_START_KST,
        specialModalEndKst: process.env.REACT_APP_PREREGISTER_MAINTENANCE_MODAL_END_KST,
        specialModalMessage: '<h1 style="font-size:18px;">2026.08.27 (목) GRAND OPEN</h1><p>\n곧 새로운 모험이 시작됩니다!\n\nBE@RBRICK HEROES와 함께할\n모험을 준비 중이니 조금만 기다려 주세요!</p>',
    });

    const SPRITE_CONFIG = {
            introStart: 1,         // 인트로 시작 프레임 (1부터 시작)
            introEnd: 61,          // 인트로 끝 프레임
            loopStart: 62,         // 루프 시작 프레임
            loopEnd: 161,           // 루프 끝 프레임
            outroStart: 162,        // 아웃트로(마우스 아웃) 시작 프레임
            outroEnd: 180,          // 아웃트로 끝 프레임
            frameHeight:80,      // 각 프레임 높이 (px)
            fps: 30,               // 초당 프레임 수 (frames per second)
            buttonWidth: "max-content",      // 버튼 너비 (px)
            buttonHeight: 80      // 버튼 높이 (px)
        };
    const frameInterval = 1000 / SPRITE_CONFIG.fps;

    useEffect(() => {
        frameRef.current = SPRITE_CONFIG.introStart;
        isIntroPlayed.current = false;
        isMouseOver.current = false;

        const btnMove = () => {
            if (!isIntroPlayed.current) {
                if (!isMouseOver.current) {
                    if (btnRef.current) {
                        btnRef.current.style.backgroundPosition = `0px -${(SPRITE_CONFIG.introStart - 1) * SPRITE_CONFIG.frameHeight}px`;
                    }
                    return;
                }

                frameRef.current++;
                if (frameRef.current > SPRITE_CONFIG.introEnd) {
                    isIntroPlayed.current = true;
                    frameRef.current = SPRITE_CONFIG.loopStart;
                }
            } else if (isMouseOver.current) {
                frameRef.current++;
                if (frameRef.current > SPRITE_CONFIG.loopEnd) {
                    frameRef.current = SPRITE_CONFIG.loopStart;
                }
            } else {
                frameRef.current++;
                if (frameRef.current > SPRITE_CONFIG.outroEnd) {
                    frameRef.current = SPRITE_CONFIG.introStart;
                    isIntroPlayed.current = false;
                }
            }

            if (btnRef.current) {
                btnRef.current.style.backgroundPosition = `0px -${(frameRef.current - 1) * SPRITE_CONFIG.frameHeight}px`;
            }
        };

        animateRef.current = window.setInterval(btnMove, frameInterval);
        return () => {
            if (animateRef.current) {
                clearInterval(animateRef.current);
            }
        };
    }, [frameInterval]);

    useEffect(() => {
        return () => {
            if (hoverAudioRef.current) {
                hoverAudioRef.current.pause();
                hoverAudioRef.current.currentTime = 0;
            }
        };
    }, []);

    const playHoverSound = () => {
        if (!hoverAudioRef.current) {
            hoverAudioRef.current = new Audio(`${process.env.PUBLIC_URL}/audio/ui_playnow.wav`);
            hoverAudioRef.current.loop = true;
        }

        hoverAudioRef.current.currentTime = 0;
        hoverAudioRef.current.play().catch(() => {});
    };

    const stopHoverSound = () => {
        if (!hoverAudioRef.current) return;
        hoverAudioRef.current.pause();
        hoverAudioRef.current.currentTime = 0;
    };

    const handleBtnMouseEnter = () => {
        isMouseOver.current = true;
        setIsImgGlitch(true);
        playHoverSound();

        if (btnRef.current) {
            btnRef.current.style.animation = 'none';
            setTimeout(() => {
                if (btnRef.current) {
                    btnRef.current.style.animation = '';
                }
            }, 10);
        }

        if (!isIntroPlayed.current || frameRef.current >= SPRITE_CONFIG.outroStart) {
            frameRef.current = SPRITE_CONFIG.introStart;
            isIntroPlayed.current = false;
        }
    };

    const handleBtnMouseLeave = () => {
        isMouseOver.current = false;
        setIsImgGlitch(false);
        stopHoverSound();
        if (isIntroPlayed.current) {
            frameRef.current = SPRITE_CONFIG.outroStart;
        } else {
            frameRef.current = SPRITE_CONFIG.introStart;
        }
    };

    const isPortraitMobile = isMobile && !isLandscapeMobile;
    const resolvedVideoSrc = isPortraitMobile ? mobileVideoSrc : pcVideoSrc;

    const handleLandingVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const target = e.target as HTMLVideoElement;
        setUseImageFallbackByError(true);
        target.style.display = 'none';
    };

    const LnchStrtFunc = async (gameSvcCd: string) => {
        try {
            if (mntnData) {
                const gameMntnInfo = mntnData?.find((item: any) => item.gameSvcCd?.toUpperCase() === gameSvcCd?.toUpperCase());
                if (gameMntnInfo && gameMntnInfo.isMntn === "Y" && tstrData?.yn !== "Y") {
                    openResultModal(langData?.COMM_MAIN_MNTN_MAIN_LBL_00 || "현재 점검 중입니다.");
                    return;
                }
            }

            const firstResp = await LnchStrtApi(gameSvcCd,null);
            switch (firstResp.apiHdr.code) {
                case "GAME0000":
                    if (window.fbq) {
                            window.fbq('trackCustom', 'BBO_FB_Game_Start');
                        }
                        const tsKst = getKstTimestamp();
                        const adPayload = fetchAdPayload( gameSvcCd||'BBO');
                        try {

                            gtmPush('BBO_GA_Game_Start', {
                                ts: tsKst,
                                ...adPayload,
                            });
                        } catch (gtmErr) {
                            if (typeof window !== 'undefined') {
                                window.dataLayer = window.dataLayer || [];
                                window.dataLayer.push({
                                    event: 'BBO_GA_Game_Start',
                                    ts: tsKst,
                                    ...adPayload,
                                });
                            }
                        }
                    stopBackgroundMusic();
                    window.location.href = firstResp.apiBody.lnchScheme;
                    break;
                case "GAME3101":
                    await GameSvcLinkApi(gameSvcCd);
                    {
                        const retryResp = await LnchStrtApi(gameSvcCd,null);
                        if (retryResp.apiHdr.code === "GAME0000") {
                            const tsKst = getKstTimestamp();
                            const adPayload = fetchAdPayload( gameSvcCd||'BBO');
                            if (window.fbq) {
                                window.fbq('trackCustom', 'BBO_FB_Game_Start');
                            }
                            try {

                                gtmPush('BBO_GA_Game_Start', {
                                    ts: tsKst,
                                    ...adPayload,
                                });
                            } catch (gtmErr) {
                                if (typeof window !== 'undefined') {
                                    window.dataLayer = window.dataLayer || [];
                                    window.dataLayer.push({
                                        event: 'BBO_GA_Game_Start',
                                        ts: tsKst,
                                        ...adPayload,
                                    });
                                }
                            }
                            stopBackgroundMusic();
                            window.location.href = retryResp.apiBody.lnchScheme;
                        } else {
                            openResultModal(`${retryResp.apiHdr.msg} `);
                        }
                    }
                    break;
                default:
                    openResultModal(`${firstResp.apiHdr.msg} `);
                    break;
            }
            // SubHdrUi처럼 게임 실행 시 모달 표시
            setShowLaunchModal(true);
        } catch (error) {
            if ((error as any).code === "ATHN2303") {
                return;
            }
            console.error('게임 실행 중 오류:', error);
            openResultModal(langData?.GAME_LAUNCH_ERROR || "게임 실행 중 오류가 발생했습니다.");
        }
    };

    return(
        <>
            <ApiResultModal
                open={resultModalOpen}
                message={resultModalMessage}
                onClose={closeResultModal}
                confirmText={langData && langData.COMM_MAIN_CHK_MAIN_BTN_00}
            />
            <Helmet>
                <title>BE@RBRICK HEROES</title>
            </Helmet>
            <div className='LandingPageWrap'>
                {!(USE_IMAGE_FALLBACK || useImageFallbackByError) ? (
                    <video
                        key={resolvedVideoSrc}
                        src={resolvedVideoSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload noplaybackrate"
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={handleLandingVideoError}
                    />
                ) : (
                isLandscapeMobile ?
                    <img
                        src={pcImageSrc}
                        alt=""
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ width: '100%', height: '100%'}}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                        }}
                    /> : isMobile
                    ? <img
                        src={mobileImageSrc}
                        alt=""
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ width: '100%', height: '100%'}}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                        }}
                    />
                    : <img
                        src={pcImageSrc}
                        alt=""
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ width: '100%', height: '100%'}}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                        }}
                    />
                )}
            <div className='landingStartBtnWrap'>
                <button 
                    ref={btnRef}
                    className="gameStartBtn colNoneBtn landingStartBtn" 
                    onClick={() => { 
                        if (!gameSvcCdState) {
                            return;
                        }

                        runWithMaintenanceGuard(() => {
                            LnchStrtFunc(gameSvcCdState);
                            setShowLaunchModal(true);
                            setGameMainPageInfoState({});
                        });
                    }}
                    onMouseEnter={handleBtnMouseEnter}
                    onMouseLeave={handleBtnMouseLeave}
                    style={{
                        backgroundImage: `url()`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '0px 0px',
                        backgroundSize: `${SPRITE_CONFIG.buttonWidth}`,
                        width: `${SPRITE_CONFIG.buttonWidth}`,
                        height: `${SPRITE_CONFIG.buttonHeight}px`,
                        minWidth: `${SPRITE_CONFIG.buttonWidth}`,
                        minHeight: `${SPRITE_CONFIG.buttonHeight}px`,
                        transition: 'none',
                        filter: isMaintenance ? 'grayscale(100%)' : 'none',
                        cursor: isMaintenance ? 'not-allowed' : 'pointer',
                        opacity: isMaintenance ? 0.6 : 1,
                        overflow: 'hidden',
                        display: isMobile ? 'none' : 'block',
                    } as React.CSSProperties}
                >
                    <GlitchImage
                        src={`${process.env.PUBLIC_URL}/images/bearbrick/landingPlaynowBtn.png`}
                        alt="Play Now"
                        width={SPRITE_CONFIG.buttonWidth}
                        height={SPRITE_CONFIG.buttonHeight}
                        isGlitch={isImgGlitch}
                    />
                </button>

                    <ul  className='deviceIconWrap'>
                        <li>
                            <img src={process.env.PUBLIC_URL + "/images/bearbrick/WindowIcon.png"} alt="" />
                        </li>
                        <li>
                            <img src={process.env.PUBLIC_URL + "/images/bearbrick/MacIcon.png"} alt="" />
                        </li>
                    </ul>
                </div>
                <p className='packageCopy'>
                    © 2026 MEDICOM TOY
                </p>
            </div>
            {showLaunchModal && (
                <GameLnchMdalUi 
                    gameMainPageInfoState={gameMainPageInfoState} 
                    langData={langData}
                    onClose={() => setShowLaunchModal(false)}
                />
            )}
        </>
    )
};

export default GamePackageUi;